import { useState, useEffect } from 'react'
import { isHTTPError } from 'ky'
import { format } from 'date-fns'
import { UTCDate } from '@date-fns/utc'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { AlertCircle, FileUp } from 'lucide-react'
import { uploadBridgeLetterApi } from '@frontend/lib/api'
import { bridgeLetterCapDate, bridgeLetterDefaultWindow } from '@common/utils/validity'
import { MAX_BRIDGE_LETTER_BYTES } from '@common/schemas'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@frontend/components/ui/dialog'
import { Button } from '@frontend/components/ui/button'
import { Spinner } from '@frontend/components/ui/spinner'

interface UploadBridgeLetterModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  reviewId: number
  vendorId: string
  /** The review's `auditPeriodEnd` (YYYY-MM-DD) — anchors the default window and the 6-month cap. */
  auditPeriodEnd: string
  vendorName?: string
}

export function UploadBridgeLetterModal({
  open,
  onOpenChange,
  reviewId,
  vendorId,
  auditPeriodEnd,
  vendorName,
}: UploadBridgeLetterModalProps) {
  const queryClient = useQueryClient()
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)

  // The window is fixed on upload: valid from the report's expiration
  // (auditPeriodEnd + 1 year) for one month; extend adds more time up to the cap.
  const { startDate, endDate } = bridgeLetterDefaultWindow(auditPeriodEnd)
  const cap = bridgeLetterCapDate(auditPeriodEnd)

  useEffect(() => {
    if (open) {
      setFile(null)
      setError(null)
    }
  }, [open])

  const mutation = useMutation({
    mutationFn: (selectedFile: File) => uploadBridgeLetterApi(reviewId, selectedFile),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review', String(reviewId)] })
      queryClient.invalidateQueries({ queryKey: ['vendor-reviews', vendorId] })
      queryClient.invalidateQueries({ queryKey: ['vendor', vendorId] })
      queryClient.invalidateQueries({ queryKey: ['vendors'] })
      onOpenChange(false)
      toast.success('Bridge letter uploaded')
    },
    onError: (err) => {
      if (isHTTPError(err)) {
        const data = err.data as { error?: string }
        setError(data.error || 'Failed to upload bridge letter')
      } else {
        setError('Failed to upload bridge letter')
      }
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!file) {
      setError('Please select a PDF file')
      return
    }
    if (file.type !== 'application/pdf') {
      setError('File must be a PDF')
      return
    }
    if (file.size > MAX_BRIDGE_LETTER_BYTES) {
      setError('File must be 20 MB or smaller')
      return
    }

    mutation.mutate(file)
  }

  const isSubmitting = mutation.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileUp className="h-5 w-5" />
            Upload Bridge Letter
          </DialogTitle>
          <DialogDescription>
            Attach a bridge letter PDF to keep {vendorName ? <strong>{vendorName}</strong> : 'this vendor'}&apos;s
            expired review usable until their next report.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* PDF file */}
          <div>
            <label htmlFor="bridge-letter-file" className="block text-sm font-medium mb-1">
              Bridge Letter PDF <span className="text-red-500">*</span>
            </label>
            <input
              id="bridge-letter-file"
              type="file"
              accept="application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="w-full text-sm file:mr-3 file:rounded-md file:border file:border-border file:bg-muted file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-muted/70"
              disabled={isSubmitting}
            />
            <p className="text-xs text-muted-foreground mt-1">PDF only, up to 20 MB.</p>
          </div>

          {/* Validity window is fixed on upload; extend adds more time afterward. */}
          <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
            <p>
              Valid from <strong>{format(new UTCDate(startDate), 'MMM d, yyyy')}</strong> through{' '}
              <strong>{format(new UTCDate(endDate), 'MMM d, yyyy')}</strong> — one month past the report&apos;s
              expiration.
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Use <strong>Extend</strong> afterward to add more time, up to {format(cap, 'MMM d, yyyy')}.
            </p>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Uploading...
                </>
              ) : (
                'Upload'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
